import { CallbackBand, Footer, PageHero } from '../components/Layout.jsx';
import { POLICIES } from '../lib/policies.js';

/**
 * Web addresses and email addresses, captured so String.split keeps them.
 * A URL may not end on punctuation — "visit www.allaboutcookies.org." should
 * link the address, not the full stop that closes the sentence.
 */
const LINKABLE = /((?:https?:\/\/|www\.)[^\s,;)]*[^\s,;.)]|[^\s@]+@[^\s@]+\.[a-z]{2,})/gi;

/**
 * Render policy copy with its addresses as links.
 *
 * The text arrives from a Word document, so allaboutcookies.org and the
 * support mailbox are bare strings. On a page whose whole job is telling
 * people how to exercise their rights, leaving them unclickable is a small but
 * real obstacle — and the same pass makes the contact block dialable-adjacent
 * without hand-marking every policy string.
 */
function withLinks(text) {
  return String(text)
    .split(LINKABLE)
    .map((part, i) => {
      // split() with one capture group alternates: even is plain text, odd is
      // a match.
      if (i % 2 === 0) return part;
      const isEmail = part.includes('@');
      const href = isEmail
        ? `mailto:${part}`
        : part.startsWith('http')
          ? part
          : `https://${part}`;
      return (
        <a
          key={i}
          href={href}
          {...(isEmail ? {} : { rel: 'noreferrer noopener', target: '_blank' })}
        >
          {part}
        </a>
      );
    });
}

/**
 * The shared legal template: short hero, centred header block, then numbered
 * cards. One component serves every policy — the Figma frame is called "Policy
 * Template" for exactly that reason.
 */
export default function PolicyPage({ policy: key }) {
  const policy = POLICIES[key];
  if (!policy) return null;

  return (
    <div className="page" data-brand="home">
      <PageHero title={policy.heroTitle} subtitle={policy.heroSubtitle} />

      <main className="page__main">
        <section className="policy">
          <div className="section-header">
            <span className="eyebrow">{policy.eyebrow}</span>
            <h2 className="section-header__title">{policy.title}</h2>
            <p className="section-header__subtitle">{policy.intro}</p>
          </div>

          {policy.pending && (
            <p className="policy__pending" role="note">
              This policy is being finalised. The summary below reflects our current practice —
              please contact us if you need anything more specific in the meantime.
            </p>
          )}

          <ol className="policy__stack">
            {policy.sections.map((section, i) => (
              <li className="policy__section" key={section.title}>
                <div className="policy__heading">
                  <span className="policy__number" aria-hidden="true">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="policy__title">{section.title}</h3>
                </div>
                {/* A block is either a paragraph or a bullet list, in the order
                    the source document sets them out. Line breaks inside a
                    paragraph are meaningful — the contact and address blocks
                    put one detail per line. */}
                <div className="policy__blocks">
                  {section.blocks.map((block, j) =>
                    Array.isArray(block) ? (
                      <ul className="policy__list" key={j}>
                        {block.map((item) => (
                          <li key={item}>{withLinks(item)}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="policy__body" key={j}>
                        {withLinks(block)}
                      </p>
                    ),
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>
      </main>

      <CallbackBand brand="home" />
      <Footer />
    </div>
  );
}
